import styles from './styles.module.scss';
import { useCallback, useEffect, useMemo } from 'react';
import MethodCard from './MethodCard';
import {
  CoinType,
  DepositMethod,
  WithdrawMethod,
} from '@starsoft/common/models';
import { FormGroup } from '@starsoft/common/components';
import { TransactionMethodsProps } from './props';
import { useModalNavigation, useSearchParams } from '@starsoft/common/hooks';
import { useCurrentModal } from '../../../../../hooks/modals/useCurrentModal';
import { CommonModalsKey } from '@starsoft/common/enums';

/**
 * TransactionMethods Component
 * This component is responsible for rendering a list of transaction methods.
 * It handles the display of method cards and skeletons based on loading state and coin type.
 *
 * @param {TransactionMethodsProps} props - The properties for the component.
 * @param {string} props.label - The label for the form group.
 * @param {boolean} props.isLoading - Indicates if the data is still loading.
 * @param {(WithdrawMethod | DepositMethod)[]} props.transactionMethods - The list of transaction methods.
 *
 * @returns {JSX.Element | null} The rendered component displaying the transaction methods or null if conditions are not met.
 *
 * @example
 * // Usage example:
 * <TransactionMethods
 *   label="Select Method"
 *   isLoading={false}
 *   transactionMethods={[{ id: 1, name: 'Method 1' }, { id: 2, name: 'Method 2' }]}
 * />
 */
export default function TransactionMethods({
  label,
  isLoading,
  transactionMethods,
}: TransactionMethodsProps) {
  const search: URLSearchParams = useSearchParams();
  const modal: string | undefined = useCurrentModal(CommonModalsKey);
  const coinType: CoinType =
    (search.get('coin-type') as CoinType) ?? CoinType.Fiat;
  const { navigateToModal } = useModalNavigation();

  /**
   * onClickMethod function
   * This function is triggered when a transaction method is clicked.
   * It updates the search parameters and navigates to the modal with the selected method.
   *
   * @param {WithdrawMethod | DepositMethod} transactionMethod - The transaction method that was clicked.
   */
  const onClickMethod = useCallback(
    (transactionMethod: WithdrawMethod | DepositMethod) => {
      search.set('method-id', `${transactionMethod.id}`);
      navigateToModal(modal, `?${search.toString()}`);
    },
    [navigateToModal, modal, search],
  );

  /**
   * onFetchTransactionMethods function
   * This function is used to fetch and set the initial transaction method if available.
   * It sets the first method in the list as the selected method in the search parameters.
   */
  function onFetchTransactionMethods() {
    if (!transactionMethods || transactionMethods?.length === 0) {
      return;
    }

    search.set('method-id', `${transactionMethods?.[0]?.id}`);
    navigateToModal(modal, `?${search.toString()}`);
  }

  // Fetch transaction methods when the component mounts or when transactionMethods change
  //eslint-disable-next-line
  useEffect(onFetchTransactionMethods, [transactionMethods]);

  /**
   * Methods array
   * This memoized array contains JSX elements for each transaction method.
   * It maps over the transactionMethods and creates a MethodCard for each.
   *
   * @returns {JSX.Element[]} An array of MethodCard components.
   */
  const Methods: JSX.Element[] = useMemo(
    () =>
      transactionMethods?.map(withdrawalMethod => (
        <MethodCard
          key={`withdraw-method-${withdrawalMethod?.id}`}
          method={withdrawalMethod}
          onClick={onClickMethod}
        />
      )),
    [transactionMethods, onClickMethod],
  );

  /**
   * Skeletons array
   * This memoized array contains JSX elements for skeleton loading states.
   * It creates a fixed number of MethodCard components with loading state.
   *
   * @returns {JSX.Element[]} An array of MethodCard components in loading state.
   */
  const Skeletons: JSX.Element[] = useMemo(
    () =>
      Array.from({
        length: 5,
      }).map((_, i) => (
        <MethodCard
          isLoading
          method={null}
          onClick={() => {}}
          key={`withdraw-method-skeleton-${i}`}
        />
      )),
    [],
  );

  // If the coin type is Crypto or there are no methods and not loading, return null
  if (coinType == CoinType.Crypto || (!isLoading && Methods?.length == 0)) {
    return null;
  }

  // Render the FormGroup with either loading skeletons or the method cards
  return (
    <FormGroup label={label}>
      <div className={styles.container}>{isLoading ? Skeletons : Methods}</div>
    </FormGroup>
  );
}
